import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import ViewPDFDialog from 'components/dialogs/ViewPDFDialog';
import UploadFileDialog from 'components/dialogs/UploadFileDialog';
import UploadPDFDialog from 'components/dialogs/UploadPDFDialog';
import DateDialog from 'components/dialogs/DateDialog';
import useModal from 'hooks/useModal';
import { CarModel } from 'models/StockModel';
import ExpCertTrackDialog from 'modules/cars/dialogs/ExpCertTrackDialog';
import ExpCertDataDialog from 'modules/cars/dialogs/ExpCertDataDialog';
import Api from 'utils/api';


const useCarDocModals = () => {
    const {t} = useTranslation('cars');
    const {showModal, hideModal} = useModal();

    const openUploadExpCertModal = (car: CarModel) => showModal(UploadPDFDialog,
        {
            title: t('export_certificate'),
        },
        {
            title: t('upload_export_certificate_for', {value: car.chassisNumber}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(file => Api.cars.uploadDocument(car.id, 'export_certificate', file))
        .then(hideModal);

    const openEditExpCertDataModal = (car: CarModel) => showModal(ExpCertDataDialog,
        {car},
        {
            title: t('export_certificate'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(data => Api.cars.storeExpCertData(car.id, data))
        .then(hideModal);

    const openUploadPriceConfirmationModal = (car: CarModel) => {
        return (car.documents.priceConfirmation ?
                Api.cars.getDocument(car.id, 'price_confirmation') :
                Promise.resolve(() => ({}))
        )
            .then(data => showModal(UploadPDFDialog,
                {
                    title: t('upload_price_confirmation_description'),
                    url: data.url,
                },
                {
                    title: t('upload_price_confirmation_for', {value: car.chassisNumber}),
                    maxWidth: 'sm',
                    fullWidth: true,
                }))
            .then(file => Api.cars.uploadDocument(car.id, 'price_confirmation', file))
            .then(hideModal)
    };

    const openUploadRepairEstimateModal = (car: CarModel) => {
        return (car.documents.repairEstimate ?
                Api.cars.getDocument(car.id, 'repair_estimate') :
                Promise.resolve(() => ({}))
        )
            .then(data => showModal(UploadPDFDialog,
                {
                    title: t('pdf_or_image_file'),
                    url: data.url,
                },
                {
                    title: t('upload_repair_estimate_for', {value: car.chassisNumber}),
                    maxWidth: 'sm',
                    fullWidth: true,
                }))
            .then(file => Api.cars.uploadDocument(car.id, 'repair_estimate', file))
            .then(hideModal)
    };

    const openUploadExplanatoryNoteModal = (car: CarModel) => {
        return (car.documents.explanatoryNote ?
                Api.cars.getDocument(car.id, 'explanatory_note') :
                Promise.resolve(() => ({}))
        )
            .then(data => showModal(UploadPDFDialog,
                {
                    title: t('pdf_or_image_file'),
                    url: data.url,
                },
                {
                    title: t('upload_explanatory_note_for', {value: car.chassisNumber}),
                    maxWidth: 'sm',
                    fullWidth: true,
                }))
            .then(file => Api.cars.uploadDocument(car.id, 'explanatory_note', file))
            .then(hideModal)
    };

    const openUploadVehicleInvoiceModal = (car: CarModel) => showModal(UploadFileDialog,
        {
            title: t('upload_invoice_description'),
            filter: files => files.filter(f => /\.(xlsx|xls|jpe?g|png)$/i.test(f.name))
        },
        {
            title: t('upload_invoice_for', {value: car.chassisNumber}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(files => Api.cars.uploadDocument(car.id, 'vehicle_invoice', files[0]))
        .then(hideModal);

    const openUploadVehicleCatalogModal = (car: CarModel) => showModal(UploadFileDialog,
        {
            title: t('catalog_in_pdf'),
            filter: files => files.filter(f => /\.(pdf|jpe?g|png)$/i.test(f.name))
        },
        {
            title: t('upload_catalog_for', {value: car.chassisNumber}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(files => Api.cars.uploadDocument(car.id, 'vehicle_catalog', files[0]))
        .then(hideModal);

    const openAddCertTrackModal = (car: CarModel) => showModal(ExpCertTrackDialog,
        {car},
        {
            title: t('add_tracking_number_for_ec', {value: car.chassisNumber}),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(Api.cars.addExpCertTrackCode)
        .then(hideModal);

    const openCertArrivedModal = (car: CarModel) => showModal(DateDialog,
        {
            description: `${car.model} ${car.chassisNumber}`,
            required: true,
            defaultDate: DateTime.now(),
        },
        {
            title: t('original_arrived'),
            maxWidth: 'xs',
        })
        .then(date => Api.cars.expCertArrived(car.id, date))
        .then(hideModal);

    const openViewPDFModal = (data: { url: string }) => showModal(ViewPDFDialog,
        data,
        {
            maxWidth: 'lg',
            fullWidth: true,
        }
    );


    return {
        openUploadExpCertModal,
        openEditExpCertDataModal,
        openUploadPriceConfirmationModal,
        openUploadRepairEstimateModal,
        openUploadExplanatoryNoteModal,
        openUploadVehicleInvoiceModal,
        openUploadVehicleCatalogModal,
        openAddCertTrackModal,
        openCertArrivedModal,
        openViewPDFModal,
    };
};

export default useCarDocModals;
